import { useEffect, useMemo, useRef } from "react";
import { cn } from "../../utils";
import {
  buildMonthGrid,
  chunkWeeks,
  isSameMonth,
  ymd,
  type WeekStart,
} from "../dateCore";
import {
  formatDayLabel,
  formatMonthCaption,
  getWeekdayNames,
  type LocaleTag,
} from "../format";
import { PART_CLASSES, stateAttr } from "../dateTimePickerStyles";
import { useCalendarKeyboard } from "../useCalendarKeyboard";
import type { UseDateTimePickerReturn } from "../useDateTimePicker";
import type { DateTimePickerClassNames, RenderDay } from "../types";
import { MonthCaption } from "./MonthCaption";

// The month grid. Disabled days are `aria-disabled`, not `disabled`, which
// would leave the tab order and trap a keyboard user before a blocked span.

interface CalendarPanelProps {
  field: UseDateTimePickerReturn;
  locale?: LocaleTag;
  weekStartsOn?: WeekStart;
  showOutsideDays?: boolean;
  fixedWeeks?: boolean;
  captionLayout?: "dropdown" | "label";
  classNames?: DateTimePickerClassNames;
  renderDay?: RenderDay;
  onEscape?: () => void;
  /** Take DOM focus on mount -- used when the popover opens. */
  autoFocus?: boolean;
}

export function CalendarPanel({
  field,
  locale,
  weekStartsOn = 0,
  showOutsideDays = true,
  fixedWeeks = true,
  captionLayout = "dropdown",
  classNames,
  renderDay,
  onEscape,
  autoFocus = false,
}: CalendarPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { onKeyDown } = useCalendarKeyboard({
    focusedDay: field.focusedDay,
    setFocusedDay: field.setFocusedDay,
    selectDay: field.selectDay,
    weekStartsOn,
    navStart: field.navStart,
    navEnd: field.navEnd,
    onEscape,
  });

  const { weekdayNames, weekdayLongNames } = useMemo(
    () => ({
      weekdayNames: getWeekdayNames(locale, weekStartsOn, "short"),
      weekdayLongNames: getWeekdayNames(locale, weekStartsOn, "long"),
    }),
    [locale, weekStartsOn],
  );

  const focusedKey = ymd(field.focusedDay);

  // `autoFocus` claims focus once, on mount. After that we only follow
  // `focusedDay` while focus is already inside the calendar -- claiming it
  // again on every day change would yank focus back from wherever the user is.
  const autoFocused = useRef(false);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const claimFocus = Boolean(autoFocus) && !autoFocused.current;
    if (!claimFocus && !root.contains(document.activeElement)) return;
    if (claimFocus) autoFocused.current = true;
    const target = root.querySelector<HTMLButtonElement>(
      `[data-day="${focusedKey}"]`,
    );
    if (target && target !== document.activeElement) target.focus();
  }, [focusedKey, autoFocus]);

  const showDropdowns =
    captionLayout === "dropdown" && field.months?.length === 1;

  return (
    <div
      ref={containerRef}
      data-part="calendar"
      className={cn(PART_CLASSES.calendar, classNames?.calendar)}
    >
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {field?.months
          ?.map((month) => formatMonthCaption(month, locale))
          .join(", ")}
      </div>

      <div
        data-part="months"
        className={cn(PART_CLASSES.months, classNames?.months)}
      >
        {field.months?.map((month, paneIndex) => {
          const cells = buildMonthGrid(month, { weekStartsOn, fixedWeeks });
          const weeks = chunkWeeks(cells);
          const caption = formatMonthCaption(month, locale);

          return (
            <div
              key={`${month.getFullYear()}-${month.getMonth()}`}
              data-part="month"
              className={cn(PART_CLASSES.month, classNames?.month)}
            >
              <MonthCaption
                month={month}
                locale={locale}
                showDropdowns={showDropdowns}
                navStart={field.navStart}
                navEnd={field.navEnd}
                canGoPrev={field.canGoPrev}
                canGoNext={field.canGoNext}
                canGoPrevYear={field.canGoPrevYear}
                canGoNextYear={field.canGoNextYear}
                showPrev={paneIndex === 0}
                showNext={paneIndex === field.months.length - 1}
                onPrev={() => field.goToMonth(-1)}
                onNext={() => field.goToMonth(1)}
                onPrevYear={() => field.goToMonth(-12)}
                onNextYear={() => field.goToMonth(12)}
                onMonthChange={field.setViewMonth}
                classNames={classNames}
              />

              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */}
              <div role="grid" aria-label={caption} onKeyDown={onKeyDown}>
                <div
                  role="row"
                  data-part="weekdays"
                  className={cn(PART_CLASSES.weekdays, classNames?.weekdays)}
                >
                  {weekdayNames.map((name, index) => (
                    <span
                      key={name + index}
                      role="columnheader"
                      aria-label={weekdayLongNames[index]}
                      data-part="weekday"
                      className={cn(PART_CLASSES.weekday, classNames?.weekday)}
                    >
                      {name}
                    </span>
                  ))}
                </div>

                {weeks.map((week) => {
                 
                  const blankRow =
                    !showOutsideDays &&
                    week.every((day) => !isSameMonth(day, month));

                  return (
                    <div
                      key={ymd(week[0])}
                      role="row"
                      aria-hidden={blankRow || undefined}
                      data-part="week"
                      data-state={blankRow ? "blank" : undefined}
                      className={cn(PART_CLASSES.week, classNames?.week)}
                    >
                      {week.map((day) => {
                        const state = field.getDayState(day, month);
                        const key = ymd(day);
                        const hidden = state.outside && !showOutsideDays;

                        if (hidden) {
                          return (
                            <div
                              key={key}
                              role="gridcell"
                              aria-hidden
                              data-part="day"
                              data-state="hidden"
                              className={cn(
                                PART_CLASSES.day,
                                classNames?.day,
                                "invisible",
                              )}
                            />
                          );
                        }

                        const defaultContent = <span>{day.getDate()}</span>;

                        return (
                          <div
                            key={key}
                            role="gridcell"
                            aria-selected={state.selected}
                            data-part="day"
                            data-state={stateAttr(state)}
                            className={cn(PART_CLASSES.day, classNames?.day)}
                          >
                            <button
                              type="button"
                              data-day={key}
                              tabIndex={state.focused ? 0 : -1}
                              aria-label={formatDayLabel(day, locale)}
                              aria-disabled={state.disabled || undefined}
                              aria-current={state.today ? "date" : undefined}
                              onClick={() => field.selectDay(day)}
                              onFocus={() => field.setFocusedDay(day)}
                              onMouseEnter={() => {
                                if (field.isPickingRange)
                                  field.setHoverDay(day);
                              }}
                              className={cn(
                                PART_CLASSES.dayButton,
                                classNames?.dayButton,
                                state.rangeMiddle && PART_CLASSES.rangeMiddle,
                                state.rangeMiddle && classNames?.rangeMiddle,
                                state.selected &&
                                  !state.rangeMiddle &&
                                  PART_CLASSES.daySelected,
                                state.selected &&
                                  !state.rangeMiddle &&
                                  classNames?.daySelected,
                                state.rangeStart &&
                                  state.rangeEnd === false &&
                                  PART_CLASSES.rangeStart,
                                state.rangeStart && classNames?.rangeStart,
                                state.rangeEnd &&
                                  state.rangeStart === false &&
                                  PART_CLASSES.rangeEnd,
                                state.rangeEnd && classNames?.rangeEnd,
                                state.today &&
                                  !state.selected &&
                                  !field.hasValue &&
                                  PART_CLASSES.dayToday,
                                state.today &&
                                  !state.selected &&
                                  !field.hasValue &&
                                  classNames?.dayToday,
                                state.outside &&
                                  !state.selected &&
                                  PART_CLASSES.dayOutside,
                                state.outside && classNames?.dayOutside,
                                state.disabled && PART_CLASSES.dayDisabled,
                                state.disabled && classNames?.dayDisabled,
                              )}
                            >
                              {renderDay
                                ? renderDay({
                                    date: day,
                                    state,
                                    defaultContent,
                                  })
                                : defaultContent}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
